import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getUsers, createUser, updateUser, deleteUser, getLocations } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
    Users,
    Plus,
    Pencil,
    Trash,
    MagnifyingGlass,
    ShieldCheck,
    UserCircle,
    MapPin,
    Envelope,
    Phone
} from '@phosphor-icons/react';

const UsersPage = () => {
    const { user: currentUser, isAdmin, fetchUser, fetchPermissions } = useAuth();
    const [users, setUsers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [roles, setRoles] = useState([]);  // catálogo dinámico desde /api/roles
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        role: 'seller',
        custom_role_id: '',  // ID del rol seleccionado (sistema o personalizado)
        location_id: '',
        is_active: true,
        commission_percent: 0,
        commission_eligible: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [usersRes, locationsRes, rolesRes] = await Promise.all([
                getUsers(),
                getLocations(),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/roles`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                })
            ]);
            setUsers(usersRes.data);
            setLocations(locationsRes.data);
            setRoles(rolesRes.data || []);
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingUser) {
                const updateData = { ...formData };
                // Email no se puede cambiar — quitarlo del update
                delete updateData.email;
                // Si el admin dejó la contraseña vacía, NO la mandamos (no se resetea)
                // Si escribió algo, sí la enviamos para que el backend la cambie
                const passwordChanged = !!(updateData.password && updateData.password.trim());
                if (!passwordChanged) {
                    delete updateData.password;
                }
                await updateUser(editingUser.id, updateData);
                toast.success(passwordChanged ? 'Usuario y contraseña actualizados' : 'Usuario actualizado');

                // Cierra el modal y limpia el form ANTES de cualquier refetch para evitar
                // que un re-render de la tabla rompa el dialog que está cerrándose.
                setShowModal(false);
                resetForm();

                // Si el admin se editó a sí mismo (cambio de rol/permisos/password),
                // refrescamos el AuthContext para que el sidebar y las rutas se mantengan en sync.
                if (currentUser && currentUser.id === editingUser.id) {
                    try {
                        await fetchUser();
                        await fetchPermissions();
                    } catch (_) {
                        /* si falla, el JWT sigue válido — no rompemos la UI */
                    }
                }
                await fetchData();
            } else {
                await createUser(formData);
                toast.success('Usuario creado');
                setShowModal(false);
                resetForm();
                await fetchData();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al guardar');
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name,
            email: user.email,
            password: '',
            phone: user.phone || '',
            role: user.role,
            custom_role_id: user.custom_role_id || '',
            location_id: user.location_id || '',
            is_active: user.is_active,
            commission_percent: user.commission_percent || 0,
            commission_eligible: user.commission_eligible !== false
        });
        setShowModal(true);
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
        try {
            await deleteUser(userId);
            toast.success('Usuario eliminado');
            fetchData();
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    const resetForm = () => {
        setEditingUser(null);
        setFormData({
            name: '',
            email: '',
            password: '',
            phone: '',
            role: 'seller',
            custom_role_id: '',
            location_id: '',
            is_active: true,
            commission_percent: 0,
            commission_eligible: true
        });
    };

    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    /**
     * Cuando el admin elige un rol del dropdown:
     *  - Si es un rol del SISTEMA → guardamos system_key como `role` y limpiamos custom_role_id
     *  - Si es un rol PERSONALIZADO → guardamos `role: 'seller'` (fallback más restrictivo)
     *                                  y el ID del rol custom en `custom_role_id`
     */
    const handleRoleChange = (roleId) => {
        const role = roles.find(r => r.id === roleId);
        if (!role) return;
        if (role.is_system && role.system_key) {
            setFormData({ ...formData, role: role.system_key, custom_role_id: '' });
        } else {
            setFormData({ ...formData, role: 'seller', custom_role_id: role.id });
        }
    };

    /** Devuelve el ID del rol asignado actualmente (para preseleccionar en el Select) */
    const currentRoleId = () => {
        if (formData.custom_role_id) return formData.custom_role_id;
        const sysRole = roles.find(r => r.is_system && r.system_key === formData.role);
        return sysRole?.id || '';
    };

    const getRoleBadge = (user) => {
        // Resolvemos el nombre del rol: si es custom, vía custom_role_id; si no, vía role base
        let roleName = '';
        let isSystem = true;
        if (user.custom_role_id) {
            const r = roles.find(x => x.id === user.custom_role_id);
            roleName = r?.name || 'Personalizado';
            isSystem = false;
        } else {
            const r = roles.find(x => x.is_system && x.system_key === user.role);
            roleName = r?.name || user.role;
        }
        const colorByRole = {
            admin: 'bg-[#FB7185]/10 text-[#FB7185] border-[#FB7185]/20',
            manager: 'bg-[#1F2D4B]/10 text-[#1F2D4B] border-[#0F1B3D]/20',
            seller: 'bg-[#059669]/10 text-[#059669] border-[#059669]/20',
            customer_service: 'bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20',
            accounting_assistant: 'bg-[#D97706]/10 text-[#D97706] border-[#D97706]/20',
        };
        const style = isSystem
            ? (colorByRole[user.role] || 'bg-[#475569]/10 text-[#475569] border-[#475569]/20')
            : 'bg-[#0EA5E9]/10 text-[#0EA5E9] border-[#0EA5E9]/20';
        return { style, label: roleName };
    };

    const getLocationName = (locationId) => {
        const location = locations.find(l => l.id === locationId);
        return location?.name || '-';
    };

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F2D4B]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="users-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-editorial text-2xl sm:text-3xl font-medium text-[#1F2D4B]">
                        Usuarios
                    </h1>
                    <p className="text-[#4B5878] mt-1">
                        {users.length} usuarios registrados
                    </p>
                </div>
                
                <div className="flex gap-3">
                    <div className="relative flex-1 sm:w-64">
                        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5878]" />
                        <Input
                            placeholder="Buscar usuario..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            data-testid="users-search-input"
                            className="h-12 pl-10 rounded-xl border-[#ECE3D2]"
                        />
                    </div>
                    <Button
                        onClick={openCreateModal}
                        data-testid="create-user-btn"
                        className="h-12 px-6 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                    >
                        <Plus size={18} className="mr-2" />
                        Nuevo
                    </Button>
                </div>
            </div>

            {/* Users Table */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl overflow-hidden">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-[#F8F4EC] hover:bg-[#F8F4EC]">
                                    <TableHead className="font-semibold text-[#1F2D4B]">Usuario</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Email</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Rol</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Comisión</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Ubicación</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Estado</TableHead>
                                    <TableHead className="font-semibold text-[#1F2D4B]">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUsers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-[#4B5878]">
                                            <Users size={40} weight="duotone" className="mx-auto mb-2 opacity-50" />
                                            <p>No hay usuarios registrados</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <TableRow key={user.id} className="hover:bg-[#F8F4EC]/50">
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-[#1F2D4B] flex items-center justify-center text-white font-medium">
                                                        {user.name?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-[#1F2D4B]">{user.name}</p>
                                                        {user.phone && (
                                                            <p className="text-xs text-[#4B5878]">{user.phone}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-[#4B5878]">{user.email}</TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const b = getRoleBadge(user);
                                                    return (
                                                        <Badge className={`${b.style} border`}>
                                                            {b.label}
                                                        </Badge>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                {user.commission_eligible === false ? (
                                                    <span className="text-xs italic text-[#8B8474]" data-testid={`commission-none-${user.id}`}>
                                                        Sin comisión
                                                    </span>
                                                ) : (
                                                    <span className="font-mono text-sm font-semibold text-[#1F2D4B]" data-testid={`commission-${user.id}`}>
                                                        {(user.commission_percent || 0).toFixed(1)}%
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-[#4B5878]">
                                                {getLocationName(user.location_id)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={user.is_active 
                                                    ? 'bg-[#D1FAE5] text-[#059669] border border-[#059669]/20' 
                                                    : 'bg-red-50 text-[#DC2626] border border-[#DC2626]/20'
                                                }>
                                                    {user.is_active ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEdit(user)}
                                                        data-testid={`edit-user-${user.id}`}
                                                        className="text-[#1F2D4B] hover:text-[#FB7185] hover:bg-[#FB7185]/10"
                                                    >
                                                        <Pencil size={16} />
                                                    </Button>
                                                    {isAdmin() && user.id !== currentUser?.id && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDelete(user.id)}
                                                            data-testid={`delete-user-${user.id}`}
                                                            className="text-[#DC2626] hover:bg-red-50"
                                                        >
                                                            <Trash size={16} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Create/Edit Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-editorial text-xl text-[#1F2D4B]">
                            {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingUser ? 'Modifica los datos del usuario' : 'Ingresa los datos del nuevo usuario'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Nombre Completo</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                data-testid="user-name-input"
                                className="h-12 rounded-xl border-[#ECE3D2]"
                            />
                        </div>
                        
                        {!editingUser && (
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    required
                                    data-testid="user-email-input"
                                    className="h-12 rounded-xl border-[#ECE3D2]"
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="password">
                                {editingUser ? 'Nueva contraseña' : 'Contraseña'}
                                {editingUser && (
                                    <span className="ml-2 text-[11px] font-normal text-[#8B8474]">
                                        (déjalo vacío para mantener la actual)
                                    </span>
                                )}
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                required={!editingUser}
                                placeholder={editingUser ? '••••••••  (sin cambios)' : 'Mínimo 6 caracteres'}
                                minLength={editingUser && !formData.password ? undefined : 6}
                                data-testid="user-password-input"
                                className="h-12 rounded-xl border-[#ECE3D2]"
                                autoComplete="new-password"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="phone">Teléfono</Label>
                            <Input
                                id="phone"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                data-testid="user-phone-input"
                                className="h-12 rounded-xl border-[#ECE3D2]"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Rol</Label>
                            <Select
                                value={currentRoleId()}
                                onValueChange={handleRoleChange}
                            >
                                <SelectTrigger data-testid="user-role-select" className="h-12 rounded-xl border-[#ECE3D2]">
                                    <SelectValue placeholder="Seleccionar rol" />
                                </SelectTrigger>
                                <SelectContent>
                                    {/* Roles del sistema primero */}
                                    {roles.filter(r => r.is_system).map(r => (
                                        <SelectItem key={r.id} value={r.id} data-testid={`role-option-${r.system_key}`}>
                                            {r.name}
                                            {r.system_key === 'admin' && !isAdmin() && ' (solo admin)'}
                                        </SelectItem>
                                    ))}
                                    {/* Separador visual si hay custom roles */}
                                    {roles.some(r => !r.is_system) && (
                                        <div className="px-2 py-1 text-[10px] uppercase text-[#8B8474] font-semibold tracking-wider">
                                            Roles personalizados
                                        </div>
                                    )}
                                    {/* Custom roles */}
                                    {roles.filter(r => !r.is_system).map(r => (
                                        <SelectItem key={r.id} value={r.id} data-testid={`role-option-custom-${r.id}`}>
                                            {r.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {formData.custom_role_id && (
                                <p className="text-[11px] text-[#8B8474]">
                                    🔧 Este usuario usará permisos personalizados del rol seleccionado
                                </p>
                            )}
                        </div>
                        
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="commission_percent">Comisión (%)</Label>
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={formData.commission_eligible === false}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            commission_eligible: !e.target.checked,
                                            commission_percent: e.target.checked ? 0 : formData.commission_percent,
                                        })}
                                        data-testid="user-no-commission-checkbox"
                                        className="h-4 w-4 rounded border-[#ECE3D2] accent-[#1F2D4B]"
                                    />
                                    <span className="text-xs font-medium text-[#1F2D4B]">Sin comisión</span>
                                </label>
                            </div>
                            <Input
                                id="commission_percent"
                                type="number"
                                step="0.1"
                                min="0"
                                max="100"
                                disabled={formData.commission_eligible === false}
                                value={formData.commission_eligible === false ? '' : formData.commission_percent}
                                onChange={(e) => setFormData({ ...formData, commission_percent: parseFloat(e.target.value) || 0 })}
                                data-testid="user-commission-input"
                                className="h-12 rounded-xl border-[#ECE3D2] font-mono disabled:bg-[#F8F4EC] disabled:text-[#8B8474]"
                                placeholder={formData.commission_eligible === false ? 'No aplica' : 'Ej. 5.0'}
                            />
                            <p className="text-[11px] text-[#8B8474]">
                                {formData.commission_eligible === false
                                    ? 'Este usuario no genera comisión por ventas. No aparecerá en cortes de comisión.'
                                    : '% sobre ventas completadas que cobra el usuario'}
                            </p>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Ubicación</Label>
                            <Select
                                value={formData.location_id}
                                onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                            >
                                <SelectTrigger data-testid="user-location-select" className="h-12 rounded-xl border-[#ECE3D2]">
                                    <SelectValue placeholder="Seleccionar ubicación" />
                                </SelectTrigger>
                                <SelectContent>
                                    {locations.map((location) => (
                                        <SelectItem key={location.id} value={location.id}>
                                            {location.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {editingUser && (
                            <div className="flex items-center justify-between p-4 bg-[#F8F4EC] rounded-xl">
                                <Label htmlFor="is_active">Usuario Activo</Label>
                                <Switch
                                    id="is_active"
                                    checked={formData.is_active}
                                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                    data-testid="user-active-switch"
                                />
                            </div>
                        )}
                        
                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowModal(false)}
                                className="flex-1 h-12 rounded-xl border-[#ECE3D2]"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                data-testid="save-user-btn"
                                className="flex-1 h-12 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                            >
                                {editingUser ? 'Guardar' : 'Crear'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UsersPage;
